import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type RevenueSyncIncidentStatus = 'OPEN' | 'RESOLVED'

export type RevenueSyncIncidentRow = {
  incident_key: string
  incident_id: number
  incident_type: string
  scope: 'account' | 'account_studio' | 'network'
  account: string | null
  studio_number: string | null
  city_name: string | null
  account_fio: string | null
  source_status: string | null
  status: RevenueSyncIncidentStatus
  opened_at: string
  last_seen_at: string
  observation_count: number
  episode_count: number
  days_open: number
  metrics: Record<string, unknown>
  last_message: string | null
  notified_at: string | null
  resolved_at: string | null
  resolved_by: string | null
  resolution_code: string | null
  resolution_note: string | null
}

export async function fetchRevenueSyncIncidents(params: {
  status?: RevenueSyncIncidentStatus | null
  limit?: number
  offset?: number
} = {}): Promise<RevenueSyncIncidentRow[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_revenue_sync_incidents', {
      p_status: params.status ?? 'OPEN',
      p_limit: params.limit ?? 200,
      p_offset: params.offset ?? 0,
    })
  )) as { data: RevenueSyncIncidentRow[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function resolveRevenueSyncIncidentWithSourceStatus(params: {
  incidentKey: string
  sourceStatus: 'ACTIVE' | 'QUARANTINED' | 'RETIRED'
  reason: string
  studioNumber?: number | null
  reviewBy?: string | null
  expectedReturn?: string | null
  note?: string | null
}): Promise<{
  incident_key: string
  incident_status: RevenueSyncIncidentStatus
  resolution_code: string
  account: string
  studio_number: number | null
  source_status: string
}> {
  const hasSelectedScope = Object.prototype.hasOwnProperty.call(params, 'studioNumber')
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('resolve_revenue_sync_incident_with_source_status', {
      p_incident_key: params.incidentKey,
      p_source_status: params.sourceStatus,
      p_reason: params.reason,
      p_studio_number: params.studioNumber ?? null,
      p_source_scope: hasSelectedScope
        ? params.studioNumber == null ? 'account' : 'account_studio'
        : null,
      p_review_by: params.reviewBy ?? null,
      p_expected_return: params.expectedReturn ?? null,
      p_note: params.note ?? null,
    })
  )) as { data: Array<any> | null; error: any }

  if (error) throw error
  const row = data?.[0]
  if (!row) throw new Error('Сервер не вернул результат решения инцидента')
  return row
}

/** The additive RPC may be absent while a restored PROD ledger is deployed. */
export function isRevenueSyncIncidentResolutionRpcMissing(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const candidate = error as { code?: unknown; message?: unknown }
  return (
    candidate.code === 'PGRST202' ||
    (typeof candidate.message === 'string' &&
      candidate.message.includes('resolve_revenue_sync_incident_with_source_status'))
  )
}
